import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Zap, Crown, ArrowRight } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import ProWelcomeModal from '../components/ProWelcomeModal';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchStatus, tier } = useSubscription();
  const { user, updateEducationFlags } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showProWelcome, setShowProWelcome] = useState(false);

  const sessionId = searchParams.get('session_id');
  const type = searchParams.get('type'); // 'subscription' or 'passes'

  useEffect(() => {
    // Refresh subscription status to get updated tier
    const refresh = async () => {
      try {
        await fetchStatus();
        // Show Pro welcome modal immediately after status is fetched
        if (type === 'subscription') {
          setShowProWelcome(true);
        }
      } catch (err) {
        console.error('Failed to refresh status:', err);
        setError('Could not refresh your account status. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    // Wait a moment for Stripe webhook to process
    setTimeout(refresh, 2000);
  }, [fetchStatus, type]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <LoadingSpinner message="Confirming your payment..." />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      fontFamily: '"DM Sans", system-ui, sans-serif'
    }}>
      <div style={{
        maxWidth: '750px',
        width: '100%',
        background: 'rgba(30, 41, 59, 0.6)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        padding: '2rem',
        border: '1px solid rgba(100, 116, 139, 0.3)',
        textAlign: 'center'
      }}>
        {/* Title */}
        <h1 style={{
          margin: '0 0 0.5rem 0',
          fontSize: '2rem',
          fontWeight: '800',
          color: '#e2e8f0'
        }}>
          Welcome to Pro
        </h1>

        {/* Message based on purchase type */}
        {type === 'subscription' && tier === 'paid' && (
          <>
            <p style={{
              margin: '0 0 1.25rem 0',
              fontSize: '1rem',
              color: '#94a3b8',
              lineHeight: '1.5'
            }}>
              Here's everything you now have access to:
            </p>
            
            <div style={{
              background: 'rgba(30, 41, 59, 0.6)',
              border: '1px solid rgba(100, 116, 139, 0.3)',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              textAlign: 'left'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '1rem'
              }}>
                <Crown size={20} color="#fbbf24" />
                <h3 style={{
                  margin: 0,
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  color: '#fbbf24'
                }}>
                  Pro Features & Benefits
                </h3>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.75rem',
                fontSize: '0.9rem',
                color: '#e2e8f0'
              }}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <Zap size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <strong>Unlimited Daily Rounds</strong>
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.15rem' }}>
                      Train as much as you want. No daily limits.
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <Zap size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <strong>All 24 Dimensions</strong>
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.15rem' }}>
                      Access every dimension and unlimited combinations.
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <Zap size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <strong>Forever Progress History</strong>
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.15rem' }}>
                      Never lose your data. Track long-term improvements.
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <Zap size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <strong>Advanced Analytics</strong>
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.15rem' }}>
                      Detailed insights into performance and trends.
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', gridColumn: '1 / -1' }}>
                  <Zap size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <strong>Unlimited Favorites</strong>
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.15rem' }}>
                      Save unlimited dimension combinations for quick access.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}


        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.2)',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            color: '#fca5a5',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        {/* CTA Button */}
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            width: '100%',
            padding: '1.25rem',
            background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
            border: 'none',
            borderRadius: '12px',
            color: 'white',
            fontSize: '1.2rem',
            fontWeight: '700',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem'
          }}
          onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
        >
          Go to Dashboard
          <ArrowRight size={24} />
        </button>

        {/* Session ID for reference */}
        {sessionId && (
          <p style={{
            margin: '1.5rem 0 0 0',
            fontSize: '0.75rem',
            color: '#64748b'
          }}>
            Order ID: {sessionId.substring(0, 20)}...
          </p>
        )}
      </div>
      
      {/* Pro Welcome Modal - Shows first with confetti */}
      <ProWelcomeModal 
        isOpen={showProWelcome}
        onClose={() => {
          setShowProWelcome(false);
          // Mark that user has seen Pro welcome
          if (user?.metadata?.educationFlags) {
            updateEducationFlags({
              ...user.metadata.educationFlags,
              hasSeenProWelcome: true
            });
          }
        }}
      />
    </div>
  );
};

export default PaymentSuccessPage;

